import * as React from "react";
import {
	Box,
	Grid,
	IconButton,
	Popover,
	Stack,
	Switch,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import InfoSVG from "../../../../assets/svgs/info.svg";
import styles from "./CensusAverageBox.module.scss";
import CardTooltipContent from "../../card/CardTooltipContent";
import HtmlTooltip from "../../../shared/HtmlTooltip";
import { toDisplayTime } from "../../../../utilis/date-formats";
import classNames from "classnames";
import { ACCOUNT_PERCENTAGE_BY } from "../../../../types/pages.type";
import { getPercentageByType } from "../../../../utilis/common";
import { useDispatch, useSelector } from "react-redux";
import { setCustomPercentageLabel, setCustomePercentages, setPercentageBy } from "../../../../store/reducers/common.slice";
import { percentageByEnum } from "../../../../data/common.data";
import AddIcon from '@mui/icons-material/Add';

import {
	usePopupState,
	bindTrigger,
	bindPopover,
} from 'material-ui-popup-state/hooks'
import CustomPercentageInput from "./CustomPercentageInput";
import { getPercentageAgainstAll } from "../../../../services/filter.service";
import { useDashboard } from "../../../../context/DashboardContext";
import aiCensusActionsService from "../../../../services/ai-census-actions.service";


const CensusAverageBox = ({
	loading,
	censusAverage,
	dbData,
	handlePercentage,
	selected = false,
	percentageAgainst,
	isLayoutMode = false,
	isMobile = false
}) => {
	const theme = useTheme();
	const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
	const { dashboardData: { isOnlyHospitalDashboard = false } } = useDashboard();
	const dispatch = useDispatch();
	const {
		bedCapacity = null,
		censusAsOfNow = null,
		censusEndDate = null,
	} = dbData;
	const { percentageBy, customPercentageLabel, customePercentages = [] } = useSelector((state) => state.common);

	React.useEffect(() => {
		if (isOnlyHospitalDashboard) {
			dispatch(setPercentageBy(percentageByEnum.bedCapacity));
		}
	}, [isOnlyHospitalDashboard, dispatch]);

	const handleParentClick = (event) => {
		event.preventDefault();
		if (event.target === event.currentTarget && handlePercentage) {
			handlePercentage && handlePercentage();
		}
	};

	const handleChangePercentage = React.useCallback((e, type, isCustom = false) => {
		if (e.target.checked) {
			isCustom && dispatch(setCustomPercentageLabel(type))
			dispatch(setPercentageBy(type));
		} else {
			isCustom && dispatch(setCustomPercentageLabel(type))
			dispatch(setPercentageBy(type));
		}
	}, [dispatch]);

	// Register handler with AI service (same pattern as DateFilterComponent)
	React.useEffect(() => {
		aiCensusActionsService.registerCensusHandler(handleChangePercentage);
		
		// Cleanup: unregister on unmount
		return () => {
			aiCensusActionsService.registerCensusHandler(null);
		};
	}, [handleChangePercentage]);

	const popupState = usePopupState({
		variant: 'popper',
		popupId: 'demoPopper',
	})

	const getAllPercentageAgainst = () => {
		getPercentageAgainstAll().then(async (res) => {
			if (res?.length > 0) {
				let resUpdated = res.map((item, index) => { return { ...item, index } })
				dispatch(setCustomePercentages(resUpdated))
			} else {
				dispatch(setCustomePercentages([]))
			}
		})
	}
	React.useMemo(() => {
		if (!popupState?.isOpen) {
			getAllPercentageAgainst()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popupState?.isOpen])

	return (
		<>
			<Box
				type="button"
				onClick={handleParentClick}
				sx={{
					border: "1px dashed #D9D9D9",
					borderRadius: 1,
					height: isMobile ? "42px" : "60px",
					padding: 1,
					display: "flex",
					justifyItems: "center",
					alignItems: "center",
					...(handlePercentage && {
						":hover": {
							border: "1px solid #4879F5",
							cursor: "pointer",
						},
					}),
					...(selected && {
						border: "1px solid #4879F5",
					}),
					cursor: isLayoutMode ? "default" : "pointer",
					pointerEvents: isLayoutMode ? "none" : "auto",
					opacity: isLayoutMode ? 0.5 : 1
				}}
			>
				<Typography
					variant="body2"
					sx={{ mt: 0, fontSize: { xs: 10, md: 12, lg: 13, xl: 15 } }}
					noWrap={false}
				>
					{getPercentageByType("y") === ACCOUNT_PERCENTAGE_BY.BED
						? "Bed capacity: "
						: `${percentageBy} of time frame selected: `}
				</Typography>

				<div
					className={classNames(
						"ffint",
						"fw700",
						"fs13",
						"df",
						"acc",
						"mla",
						"p-l-55",
						"p-r-55",
						loading && "skeleton",
						styles.infoContainer,
						!loading && "cp",
						loading && styles.skeleton
					)}
					{...bindTrigger(popupState)}
				>
					{!loading && (
						<>
							<Typography
								variant="h6"
								sx={{ '@media (max-width:1280px)': { fontSize: "0.9rem" } }}
							>
								{percentageAgainst}
							</Typography>
							{getPercentageByType("y") === ACCOUNT_PERCENTAGE_BY.CENSUS && (
								<span className={styles.infoWrapper}>
									<HtmlTooltip
										content={
											<CardTooltipContent content="The number here displays the average number of residents in the facility during the user-selected time frame." />
										}
									>
										<IconButton disableFocusRipple={true} disableRipple={true}>
											<InfoSVG fill="#4879F5" />
										</IconButton>
									</HtmlTooltip>
								</span>
							)}
						</>
					)}
				</div>
			</Box>
			{isMobileDevice ? (
				<Dialog
					{...bindPopover(popupState)}
					fullWidth
					fullScreen
					PaperProps={{
						sx: {
							backgroundColor: '#fafafa',
							borderRadius: 2,
							minHeight: '60vh',
							maxHeight: '60vh',
							width: '95vw',
							maxWidth: 'none'
						}
					}}
				>
					<DialogTitle
						sx={{
							backgroundColor: '#f8f9fa',
							borderBottom: '1px solid #e0e0e0',
							p: 1.5,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}
					>
						<Typography
							variant="h6"
							sx={{
								fontSize: '1rem',
								fontWeight: 600,
								color: '#333'
							}}
						>
							Percentage Settings
						</Typography>
						<IconButton
							onClick={popupState.close}
							size="small"
							sx={{
								color: '#666',
								'&:hover': {
									backgroundColor: '#f0f0f0'
								}
							}}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</DialogTitle>
					<DialogContent sx={{ p: 2, mt:2 }}>
						<Stack spacing={1.5}>
							<Stack 
								direction="row" 
								alignItems="flex-start" 
								spacing={0.5}
								sx={{
									width: '100%'
								}}
							>
								<Switch
									checked={percentageBy === percentageByEnum.bedCapacity}
									onChange={(e) => handleChangePercentage(e, percentageByEnum.bedCapacity)}
									inputProps={{ "aria-label": "updatedPercentage" }}
									size="small"
									sx={{ mt: 0.5, flexShrink: 0 }}
								/>
								<PopoverItem title={"Bed Capacity"} value={bedCapacity} />
							</Stack>
							
							{!isOnlyHospitalDashboard && (
								<>
									<Stack 
										direction="row" 
										alignItems="flex-start" 
										spacing={0}
										sx={{
											width: '100%'
										}}
									>
										<Switch
											checked={percentageBy === percentageByEnum.censusAsOfNow}
											onChange={(e) => handleChangePercentage(e, percentageByEnum.censusAsOfNow)}
											inputProps={{ "aria-label": "updatedPercentage" }}
											size="small"
											sx={{ mt: 0, flexShrink: 0 }}
										/>
										<PopoverItem
											title={`census as of ${toDisplayTime(censusEndDate)}`}
											value={censusAsOfNow}
										/>
									</Stack>
									<Stack 
										direction="row" 
										alignItems="flex-start" 
										spacing={0}
										sx={{
											width: '100%'
										}}
									>
										<Switch
											checked={percentageBy === percentageByEnum.censusAverage}
											onChange={(e) => handleChangePercentage(e, percentageByEnum.censusAverage)}
											inputProps={{ "aria-label": "updatedPercentageCensusAverage" }}
											size="small"
											sx={{ mt: 0, flexShrink: 0 }}
										/>
										<PopoverItem
											title={"Average census of time frame selected"}
											value={censusAverage}
										/>
									</Stack>
								</>
							)}

							<Stack spacing={1}>
								{customePercentages && customePercentages.length > 0 && customePercentages.map((item, index) => (
									<CustomPercentageInput
										key={index}
										percentageBy={percentageBy}
										customPercentageLabel={customPercentageLabel}
										handleChangePercentage={handleChangePercentage}
										item={item}
										index={index}
										customePercentages={customePercentages}
									/>
								))}
								<IconButton
									onClick={() =>
										dispatch(setCustomePercentages([
											...customePercentages, {
												index: customePercentages.length,
												customPercentage: null,
												label: ""
											}
										]))
									}
									aria-label="add"
									size="small"
									sx={{ ml: 1, mt: 0.5 }}
									color="primary"
								>
									<AddIcon fontSize="inherit" />
								</IconButton>
							</Stack>
						</Stack>
					</DialogContent>
				</Dialog>
			) : (
				<Popover
					{...bindPopover(popupState)}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center",
					}}
					PaperProps={{
						elevation: 3,
						variant: "outlined",
						rounded: true,
						sx: {
							pointerEvents: "auto",
							overflow: "visible",
							filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
							mt: 0.5,
							"&::before": {
								content: '""',
								display: "block",
								position: "absolute",
								top: 0,
								left: "56%",
								width: 20,
								height: 20,
								bgcolor: "background.paper",
								transform: "translateY(-50%) rotate(45deg)",
								zIndex: 0,
							},
						},
					}}
				>
					<Grid
						direction={"row"}
						container
						spacing={1}
						sx={{
							p: 2,
							width: "450px",
						}}
					>
						<Grid item xs={12} container>
							<Switch
								checked={percentageBy === percentageByEnum.bedCapacity}
								onChange={(e) => handleChangePercentage(e, percentageByEnum.bedCapacity)}
								inputProps={{ "aria-label": "updatedPercentage" }}
								size="small"
							/>
							<PopoverItem title={"Bed Capacity"} value={bedCapacity} />
						</Grid>
						{!isOnlyHospitalDashboard && (
							<>
								<Grid item xs={12} container key={`censusAsOfNow`}>
									<Switch
										checked={percentageBy === percentageByEnum.censusAsOfNow}
										onChange={(e) => handleChangePercentage(e, percentageByEnum.censusAsOfNow)}
										inputProps={{ "aria-label": "updatedPercentage" }}
										size="small"
									/>
									<PopoverItem
										title={`census as of ${toDisplayTime(censusEndDate)}`}
										value={censusAsOfNow}
									/>
								</Grid>
								<Grid item xs={12} container key={`censusAverage`}>
									<Switch
										checked={percentageBy === percentageByEnum.censusAverage}
										onChange={(e) => handleChangePercentage(e, percentageByEnum.censusAverage)}
										inputProps={{ "aria-label": "updatedPercentageCensusAverage" }}
										size="small"
									/>
									<PopoverItem
										title={"Average census of time frame selected"}
										value={censusAverage}
									/>
								</Grid>
							</>
						)}

						<Grid item xs={12} container key={`customPercentages`}>
							{customePercentages && customePercentages.length > 0 && customePercentages.map((item, index) => (
								<CustomPercentageInput
									percentageBy={percentageBy}
									customPercentageLabel={customPercentageLabel}
									handleChangePercentage={handleChangePercentage}
									item={item}
									index={index}
									customePercentages={customePercentages}
									isMobile={isMobile}
								/>)
							)}
							<IconButton
								onClick={() =>
									dispatch(setCustomePercentages([
										...customePercentages, {
											index: customePercentages.length,
											customPercentage: null,
											label: ""
										}
									]))
								}
								aria-label="add"
								size="small"
								sx={{ ml: 1, mt: 0.5 }}
								color="primary"
							>
								<AddIcon fontSize="inherit" />
							</IconButton>
						</Grid>
					</Grid>
				</Popover>
			)}
		</>
	);
};

export default CensusAverageBox;

const PopoverItem = ({ title, value }) => {
	const theme = useTheme();
	const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
	
	return (
		<Stack 
			direction={"row"} 
			justifyContent={"space-between"} 
			alignItems={"flex-start"}
			sx={{
				...(isMobileDevice && {
					flex: 1,
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					width: '100%',
					minWidth: 0,
					flexWrap: 'wrap',
					gap: 0
				})
			}}
		>
			<Typography 
				variant="subtitle1"
				sx={{
					...(isMobileDevice && {
						fontSize: '0.70rem',
						fontWeight: 500,
						color: '#333',
						flexShrink: 0,
						minWidth: 0,
						wordBreak: 'break-word',
						overflowWrap: 'break-word'
					})
				}}
			>
				{title} :
			</Typography>
			<Typography 
				variant="subtitle1"
				sx={{
					textAlign: "end",
					...(isMobileDevice && {
						flex: 1,
						minWidth: 0,
						wordBreak: 'break-word',
						overflowWrap: 'break-word',
						fontSize: '0.70rem',
						fontWeight: 600,
						color: '#4879F5',
						textAlign: 'right',
						ml: 1
					})
				}}
			>
				{value}
			</Typography>
		</Stack>
	);
};
